import {Component, EventEmitter, inject, input, InputSignal, ViewEncapsulation} from '@angular/core';
import {ApiAdapter, BaseControlComponent, ControlStore} from 'frontier/nucleus';
import {BehaviorSubject, concatMap, debounceTime, Observable, of, switchMap} from 'rxjs';
import {catchError, filter, finalize, map, tap} from 'rxjs/operators';
import {IEPostConfig, IEpostConfigApi} from '../epost-config-form.interface';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {FeedbackService, ReasoningDialogComponent} from 'frontier/browserkit';
import {EpostConfigFormComponent} from '../epost-config-form.component';
import {AsyncPipe} from '@angular/common';
import AppControlGuids from '../../../../api/ControlGuids';
import {EpostConfigurationService} from '../../../../api/generated';

class EPostConfigApiAdapter extends ApiAdapter {
  from(apiData: IEpostConfigApi): IEPostConfig {
    return {
      active: apiData.active,
      color: apiData.color,
      duplex: apiData.duplex,
      senderCity: apiData.sender_city,
      ekpNumber: apiData.ekp_number,
      senderPostCode: apiData.sender_postcode,
      senderName: apiData.sender_name,
      senderStreet: apiData.sender_street,
      senderHouseNumber: apiData.sender_housenumber,
    };
  }

  to(data: IEPostConfig): IEpostConfigApi {
    return {
      active: data.active,
      color: data.color,
      duplex: data.duplex,
      ekp_number: data.ekpNumber,
      sender_postcode: data.senderPostCode,
      sender_name: data.senderName,
      sender_street: data.senderStreet,
      sender_city: data.senderCity,
      sender_housenumber: data.senderHouseNumber,
    };
  }
}

@Component({
  selector: 'app-epost-config-wrapper',
  templateUrl: './epost-config-wrapper.component.html',
  styleUrls: ['./epost-config-wrapper.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [EpostConfigFormComponent, AsyncPipe],
})
export class EpostConfigWrapperComponent extends BaseControlComponent {
  private _epostService: EpostConfigurationService = inject(EpostConfigurationService);
  protected readonly feedbackService: FeedbackService = inject(FeedbackService);
  protected readonly controlStore: ControlStore = inject(ControlStore);

  readonly formChangeDebounceTime: number = 1000;

  apiAdapter: EPostConfigApiAdapter = new EPostConfigApiAdapter();
  override GUID: InputSignal<string> = input<string>(AppControlGuids.TEpostConfiguration);

  requestSmsClicked$: EventEmitter<void> = new EventEmitter();
  healthCheckClicked$: EventEmitter<void> = new EventEmitter();

  // Boolean if the form change is currently in process.
  isChangingForm$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  formChangeEvent$: EventEmitter<IEPostConfig> = new EventEmitter();
  formChange$: Observable<string> = this.formChangeEvent$
    .pipe(
      tap(() => this.isChangingForm$.next(true)),
      debounceTime(this.formChangeDebounceTime),
      map((v: IEPostConfig): IEpostConfigApi => this.apiAdapter.to(v)),
      // concatMap: a write must never be aborted by a following emission — switchMap
      // canceled the running save when the user kept typing (potentially lost writes).
      concatMap((form: IEpostConfigApi) =>
        this._epostService.epostConfigurationChangeData({InstanceId: this.apiInstance.instanceid, data: form})
          .pipe(finalize(() => {
            this.isChangingForm$.next(false)
          }))
      ),
    );

  // when request sms is clicked and no open form change request is open do:
  // request sms => open sms dialog
  smsProcess$: Observable<any> = this.requestSmsClicked$.pipe(
    switchMap(() =>
      this.isChangingForm$.pipe(filter((v: boolean): boolean => v === false)),
    ),
    concatMap(() => this.requestSms()),
    concatMap(() => this.openSmsDialog()),
    catchError((res) => {
      this.feedbackService.setError(res.error, 10000);
      return of(res);
    })
  );

  dialog: MatDialog = inject(MatDialog);

  constructor() {
    super();
  }

  override ngOnInit(): void {
    this.subs.add(
      this.formChange$.subscribe()
    );
    this.subs.add(
      this.apiInstanceInitialize.pipe(
        concatMap(() => this.changeAndFetch())
      ).subscribe()
    );
    this.subs.add(this.smsProcess$.subscribe());
    this.subs.add(this.healthCheckClicked$
      .pipe(
        switchMap(() => this.healthCheck())
      ).subscribe());

    super.ngOnInit();
  }

  /**
   * opens the sms dialog. If an error in the enter SMS api call is happened, reopen the sms dialog
   */
  openSmsDialog(): Observable<MatDialogRef<ReasoningDialogComponent>> {
    return this.dialog.open(ReasoningDialogComponent, {
      data: {
        title: 'Geben Sie bitte den SMS-Code ein, den Sie erhalten haben.'
      },
      maxWidth: '25rem'
    }).afterClosed().pipe(
      concatMap(sms => {
        if (sms) {
          return this.enterSms(sms).pipe(
            catchError((res) => {
              this.feedbackService.setError(res.error, 10000);
              return this.openSmsDialog();
            })
          );
        }
        return of(null);
      }),
    );
  }

  /**
   * API call to enter the received sms
   * @param sms the received and entered sms
   */
  enterSms(sms: string): Observable<any> {
    return this._epostService.epostConfigurationEnterSmsCode({
      InstanceId: this.apiInstance.instanceid,
      smscode: sms,
    }).pipe(
      tap(updatedForm => {
        if (typeof updatedForm === 'object') {
          this.data = updatedForm
        }
      })
    );
  }

  /**
   * API call to receive a new sms
   */
  requestSms(): Observable<string> {
    return this._epostService.epostConfigurationRequestSmsCode({InstanceId: this.apiInstance.instanceid});
  }

  /**
   * API call to check if the EPost configuration is healthy
   */
  healthCheck(): Observable<string> {
    return this._epostService.epostConfigurationHealthCheck({InstanceId: this.apiInstance.instanceid})
      .pipe(
        tap((val: string) => this.feedbackService.setNotification(val, 10000)),
        catchError((err) => {
          this.feedbackService.setError(err.error, 10000);
          return of(err);
        }),
      );
  }
}
